import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Upload, Download, Loader2, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { importDirectClubUsers, type ImportRowResult } from "@/lib/dc-import.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/import-dc")({
  component: ImportDcPage,
});

type ParsedRow = Record<string, string | boolean>;

// RFC-4180-ish CSV parser: handles quotes, escaped quotes, commas and newlines inside quotes.
function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const rows: string[][] = [];
  let cur: string[] = [];
  let field = "";
  let inQuotes = false;
  let i = 0;
  const src = text.replace(/^\uFEFF/, ""); // strip BOM
  while (i < src.length) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      field += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ",") { cur.push(field); field = ""; i++; continue; }
    if (c === "\r") { i++; continue; }
    if (c === "\n") { cur.push(field); rows.push(cur); cur = []; field = ""; i++; continue; }
    field += c; i++;
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); rows.push(cur); }
  const nonEmpty = rows.filter((r) => r.some((v) => v.trim().length > 0));
  if (nonEmpty.length === 0) return { headers: [], rows: [] };
  const headers = nonEmpty[0].map((h) => h.trim());
  return { headers, rows: nonEmpty.slice(1) };
}

function toBool(v: string | undefined): boolean | undefined {
  if (v === undefined) return undefined;
  const s = v.trim().toLowerCase();
  if (["true", "1", "yes", "y", "ano"].includes(s)) return true;
  if (["false", "0", "no", "n", "ne"].includes(s)) return false;
  return undefined;
}

function toRowObjects(headers: string[], rows: string[][]): ParsedRow[] {
  return rows.map((r) => {
    const o: ParsedRow = {};
    headers.forEach((h, idx) => {
      const val = (r[idx] ?? "").trim();
      if (h === "direct_club_member" || h === "show_on_map") {
        const b = toBool(val);
        if (b !== undefined) o[h] = b;
      } else {
        o[h] = val;
      }
    });
    return o;
  });
}

function ImportDcPage() {
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [sendInvites, setSendInvites] = useState(true);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<ImportRowResult[] | null>(null);
  const [summary, setSummary] = useState<{ total: number; created: number; updated: number; errors: number } | null>(null);
  const importFn = useServerFn(importDirectClubUsers);

  const REQUIRED = ["email", "full_name"];
  const KNOWN = [
    "email", "full_name", "preferred_language", "referrer_email",
    "referral_code", "direct_club_member", "wise_email", "bank_iban",
    "approx_country", "show_on_map",
  ];

  const missingRequired = REQUIRED.filter((k) => !headers.includes(k));
  const unknownColumns = headers.filter((h) => !KNOWN.includes(h));

  const onFileChange = async (f: File | null) => {
    setFile(f);
    setResults(null);
    setSummary(null);
    if (!f) { setHeaders([]); setRows([]); return; }
    const text = await f.text();
    const { headers: h, rows: r } = parseCsv(text);
    setHeaders(h);
    setRows(toRowObjects(h, r));
  };

  const runImport = async () => {
    if (rows.length === 0) return;
    if (missingRequired.length > 0) {
      toast.error(`Chybí povinné sloupce: ${missingRequired.join(", ")}`);
      return;
    }
    setRunning(true);
    setResults(null);
    setSummary(null);
    try {
      const redirectTo = `${window.location.origin}/auth`;
      const out = await importFn({ data: { rows, redirectTo, sendInvites } });
      setResults(out.results);
      setSummary(out.summary);
      toast.success(
        `Hotovo: ${out.summary.created} vytvořeno, ${out.summary.updated} aktualizováno, ${out.summary.errors} chyb.`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Import selhal");
    } finally {
      setRunning(false);
    }
  };

  const downloadTemplate = () => {
    const csv = [
      "email,full_name,preferred_language,referrer_email,referral_code,direct_club_member,wise_email,bank_iban,approx_country,show_on_map",
      "jan.novak@example.com,Jan Novák,cs,petr.dvorak@example.com,,TRUE,jan.novak@wise.com,CZ6508000000192000145399,CZ,TRUE",
      "maria.garcia@example.com,María García,es,,DCLUB123,TRUE,maria.g@wise.com,ES9121000418450200051332,ES,TRUE",
      "hans.mueller@example.com,Hans Müller,de,maria.garcia@example.com,BERLIN01,TRUE,,DE89370400440532013000,DE,FALSE",
      "anna.rossi@example.com,Anna Rossi,it,,,TRUE,anna.r@wise.com,IT60X0542811101000000123456,IT,TRUE",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "direct-club-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl">Import členů Direct Clubu</h1>
        <p className="text-sm text-muted-foreground">
          Nahrajte CSV z DirectClub.Digital. Existující účty (podle e-mailu) budou aktualizovány, chybějící vytvořeny.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">1. CSV soubor</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-64 flex-1">
              <Label htmlFor="csv">Vyberte soubor .csv</Label>
              <Input
                id="csv"
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
                className="mt-1"
              />
            </div>
            <Button variant="outline" onClick={downloadTemplate} className="gap-2">
              <Download className="h-4 w-4" /> Stáhnout šablonu
            </Button>
          </div>

          {file && (
            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              <div className="font-medium">{file.name}</div>
              <div className="text-muted-foreground">
                {rows.length} řádků · {headers.length} sloupců
              </div>
              {missingRequired.length > 0 && (
                <div className="mt-2 flex items-center gap-2 text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  Chybí povinné sloupce: <code>{missingRequired.join(", ")}</code>
                </div>
              )}
              {unknownColumns.length > 0 && (
                <div className="mt-2 flex items-center gap-2 text-amber-600">
                  <AlertCircle className="h-4 w-4" />
                  Neznámé sloupce (budou ignorovány): <code>{unknownColumns.join(", ")}</code>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">2. Nastavení importu</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-start gap-3 text-sm">
            <Checkbox
              checked={sendInvites}
              onCheckedChange={(v) => setSendInvites(v === true)}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium">Odeslat pozvánkový e-mail</span>
              <span className="block text-muted-foreground">
                Novým uživatelům přijde odkaz pro nastavení hesla. Vypněte pro tichý import
                (účet je vytvořen a e-mail označen jako ověřený, uživatel musí použít „Zapomenuté heslo").
              </span>
            </span>
          </label>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={runImport}
          disabled={!file || rows.length === 0 || missingRequired.length > 0 || running}
          className="gap-2"
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {running ? "Importuji…" : `Spustit import (${rows.length} řádků)`}
        </Button>
        {results && (
          <Button variant="outline" onClick={() => { setResults(null); setSummary(null); }} className="gap-2">
            <RefreshCw className="h-4 w-4" /> Vyčistit výsledek
          </Button>
        )}
      </div>

      {summary && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Výsledek importu
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-4">
              <Stat label="Celkem" value={summary.total} />
              <Stat label="Vytvořeno" value={summary.created} tone="emerald" />
              <Stat label="Aktualizováno" value={summary.updated} tone="blue" />
              <Stat label="Chyby" value={summary.errors} tone={summary.errors > 0 ? "red" : undefined} />
            </div>

            {results && results.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1">#</th>
                      <th className="px-2 py-1">E-mail</th>
                      <th className="px-2 py-1">Stav</th>
                      <th className="px-2 py-1">Doporučitel</th>
                      <th className="px-2 py-1">Poznámka</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r) => (
                      <tr key={r.index} className="border-t">
                        <td className="px-2 py-1 text-muted-foreground">{r.index + 1}</td>
                        <td className="px-2 py-1 font-mono text-xs">{r.email}</td>
                        <td className="px-2 py-1">
                          <StatusBadge status={r.status} />
                        </td>
                        <td className="px-2 py-1">
                          {r.referrer_resolved === true && <Badge variant="outline" className="text-emerald-700">nalezen</Badge>}
                          {r.referrer_resolved === false && <Badge variant="outline" className="text-amber-700">nenalezen</Badge>}
                          {r.referrer_resolved === undefined && <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-2 py-1 text-xs text-muted-foreground">{r.message ?? ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "emerald" | "blue" | "red" }) {
  const color =
    tone === "emerald" ? "text-emerald-600" :
    tone === "blue" ? "text-blue-600" :
    tone === "red" ? "text-destructive" : "";
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className={`mt-1 font-serif text-2xl ${color}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: ImportRowResult["status"] }) {
  if (status === "created") return <Badge className="bg-emerald-600">vytvořen</Badge>;
  if (status === "updated") return <Badge className="bg-blue-600">aktualizován</Badge>;
  if (status === "skipped") return <Badge variant="outline">přeskočen</Badge>;
  return <Badge variant="destructive">chyba</Badge>;
}
